import jsPDF from 'jspdf';
import { useThemeStore } from '../stores/useThemeStore';
import { useAuthStore } from '../stores/useAuthStore';
import { usePdfPrefsStore, type PdfTemplate, type PdfPrefs } from '../stores/usePdfPrefsStore';
import { formatDate, type DateInput } from '@/lib/datetime';
import { getDisplayTimeZone } from '@/stores/useTimezoneStore';

/** Zone-bound date formatter so PDF chrome honors the clinic's display zone. */
export const fmtDateTz = (v: DateInput) => formatDate(v, getDisplayTimeZone());

export type { PdfTemplate };

/**
 * Emit a finished PDF the one way the app now does it: save it to the user's
 * downloads AND open an auto-printing copy so the *PDF* — never the app DOM —
 * is what reaches the printer. This replaces every `window.print()` /
 * new-window-and-print path we used to have (which printed app chrome and
 * looked terrible).
 *
 * The downloaded file is clean (saved before autoPrint is baked in), so it
 * won't pop a print dialog when reopened later. The print copy is loaded in a
 * hidden same-origin iframe via a blob URL — this avoids the popup-blocker
 * issues `window.open()` hits when called after an `await`. The PDF viewer
 * honours the autoPrint OpenAction and opens the print dialog on the document.
 */
export function downloadAndPrintPdf(pdf: jsPDF, filename: string): void {
  // 1) Clean download (no autoPrint baked into the saved file).
  pdf.save(filename);

  // 2) Best-effort print copy. If anything here throws (older browser, no
  //    iframe), the download above already succeeded.
  try {
    pdf.autoPrint();
    const blobUrl = pdf.output('bloburl') as unknown as string;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    // Keep the iframe alive long enough for the viewer to grab the document;
    // the print dialog stays open after we tear it down.
    window.setTimeout(() => {
      iframe.remove();
      try {
        URL.revokeObjectURL(blobUrl);
      } catch {
        /* already revoked */
      }
    }, 60_000);
  } catch {
    /* print copy is best-effort */
  }
}

/**
 * Print copy for renderers that hand us a finished Blob (e.g. the billing
 * invoice renderer) rather than a live jsPDF instance. The blob should
 * already have autoPrint baked in by its producer; if not, it still opens in
 * the hidden iframe and the user can print from the viewer.
 */
export function printPdfBlob(blob: Blob): void {
  try {
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    window.setTimeout(() => {
      iframe.remove();
      try {
        URL.revokeObjectURL(blobUrl);
      } catch {
        /* already revoked */
      }
    }, 60_000);
  } catch {
    /* best-effort */
  }
}

/**
 * Shared, clinic-branded design system for every PDF the app emits.
 *
 * One visual language across prescriptions, encounter notes, orders,
 * referrals, invoices, reports, etc.: a slim brand-colour band, the
 * clinic logo + name (with optional contact letterhead), an editorial
 * header block (eyebrow + large right-aligned document title + meta),
 * a hairline rule with a brand-colour accent tick, and a white-labelled
 * footer (clinic name + timestamp + pagination — no platform mention).
 *
 * All colours derive from the tenant's primary colour so the same code
 * renders in each clinic's brand. Units here are MILLIMETRES (jsPDF's
 * default) — the billing renderer works in points and mirrors this spec
 * by proportion + identical font sizes (font sizes are points either way).
 */

export interface BrandAssets {
  hospitalName: string;
  tagline?: string;
  /** Optional clinic contact letterhead line (address · tel · email). */
  contactLine?: string;
  /** Resolved accent colour (doctor override → clinic primary → default). */
  primary: [number, number, number];
  logoDataUrl: string | null;
  logoFormat: 'PNG' | 'JPEG';
  logoAspect: number;
  /** Resolved document template (doctor override → clinic → 'editorial'). */
  template: PdfTemplate;
  /** Doctor's credentials line for signature blocks, if set. */
  providerCredentials?: string;
  /** Doctor's saved signature (base64 PNG) — fallback when none is passed. */
  signatureOverride?: string;
}

// Shared neutral palette — kept identical across all renderers so a
// stack of mixed documents reads as one family.
export const INK: [number, number, number] = [23, 23, 23];
export const MUTED: [number, number, number] = [115, 115, 115];
export const HAIRLINE: [number, number, number] = [226, 226, 226];

const DEFAULT_PRIMARY: [number, number, number] = [79, 70, 229];

// The stale @types/jspdf shadows jspdf 3.x's own typings and omits some
// instance methods (e.g. getNumberOfPages); narrow-cast at the call site.
const pageCount = (pdf: jsPDF): number =>
  (pdf as unknown as { getNumberOfPages(): number }).getNumberOfPages();

let cachedBrand: BrandAssets = {
  hospitalName: 'MAIE',
  tagline: undefined,
  contactLine: undefined,
  primary: DEFAULT_PRIMARY,
  logoDataUrl: null,
  logoFormat: 'PNG',
  logoAspect: 1,
  template: 'editorial',
  providerCredentials: undefined,
  signatureOverride: undefined,
};
let cachedLogoUrl: string | undefined = undefined;

export const hexToRgb = (hex: string): [number, number, number] | null => {
  const cleaned = (hex || '').replace('#', '').trim();
  if (!/^([0-9a-f]{6}|[0-9a-f]{3})$/i.test(cleaned)) return null;
  const full =
    cleaned.length === 3 ? cleaned.split('').map((c) => c + c).join('') : cleaned;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
};

const cssVarRgb = (name: string): [number, number, number] | null => {
  if (typeof document === 'undefined') return null;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return hexToRgb(raw);
};

/**
 * Mix a colour toward white. Used for subtle brand-tinted panels/fills
 * (table headers, KPI cards) so surfaces pick up the clinic colour
 * without overwhelming the content. amount=0 → original, 1 → white.
 */
export const lightTint = (
  rgb: [number, number, number],
  amount = 0.92,
): [number, number, number] => [
  Math.round(rgb[0] + (255 - rgb[0]) * amount),
  Math.round(rgb[1] + (255 - rgb[1]) * amount),
  Math.round(rgb[2] + (255 - rgb[2]) * amount),
];

const fetchLogoToDataUrl = async (
  url: string,
): Promise<{ dataUrl: string; aspect: number; format: 'PNG' | 'JPEG' } | null> => {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    const isSvg = blob.type === 'image/svg+xml' || /\.svg($|\?)/i.test(url);
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = () => reject(r.error);
      r.readAsDataURL(blob);
    });

    const measureImg = (src: string) =>
      new Promise<{ w: number; h: number } | null>((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
        img.onerror = () => resolve(null);
        img.src = src;
      });

    if (isSvg) {
      // jsPDF can't render SVG directly — rasterize to PNG via canvas.
      const sized = await measureImg(dataUrl);
      const w = Math.max(64, sized?.w || 256);
      const h = Math.max(64, sized?.h || 256);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      const drawn = await new Promise<boolean>((resolve) => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, w, h);
          resolve(true);
        };
        img.onerror = () => resolve(false);
        img.src = dataUrl;
      });
      if (!drawn) return null;
      return { dataUrl: canvas.toDataURL('image/png'), aspect: w / h, format: 'PNG' };
    }

    const sized = await measureImg(dataUrl);
    const aspect = sized ? sized.w / Math.max(sized.h, 1) : 1;
    const format: 'PNG' | 'JPEG' = blob.type === 'image/jpeg' ? 'JPEG' : 'PNG';
    return { dataUrl, aspect, format };
  } catch {
    return null;
  }
};

/**
 * Pull the latest tenant branding (name, tagline, logo, primary colour)
 * into the module-level cache used by the header/footer. Call at the top
 * of every exporter before drawing. Returns the resolved brand so callers
 * can read e.g. `.primary` without a second lookup.
 */
export const refreshBrandAssets = async (
  prefsOverride?: Partial<PdfPrefs>,
): Promise<BrandAssets> => {
  const { branding } = useThemeStore.getState();
  const activeTenant = useAuthStore.getState().activeTenant;

  // Resolve clinic name in priority order:
  //   1. activeTenant.displayName from auth — canonical post-login source,
  //      always present when signed in (no async fetch lag).
  //   2. branding.hospitalName from the theme store — populated by the
  //      post-auth /system-settings refetch; used pre-auth and as backup.
  //   3. 'MAIE' — platform default when nothing else is available.
  const hospitalName =
    activeTenant?.displayName?.trim() || branding.hospitalName?.trim() || 'MAIE';

  const clinicPrimary =
    hexToRgb(branding.primaryColor || '') ||
    cssVarRgb('--color-primary-600') ||
    DEFAULT_PRIMARY;

  // ── Customization layer: doctor → clinic → tenant default ──────────────
  // The clinic owns identity (logo, name); a doctor may override the
  // template, accent colour, signature and credentials line for their own
  // documents. Clinic-level template/accent defaults read from optional
  // branding fields when present (integration point — no UI surfaces them
  // yet), otherwise fall back to the platform default.
  // prefsOverride lets the settings live-preview render unsaved selections
  // without mutating the persisted store. When absent, read the real store.
  const prefs = prefsOverride ?? usePdfPrefsStore.getState();
  const clinicTemplate = branding.pdfTemplate;
  const clinicAccent = branding.pdfAccentColor
    ? hexToRgb(branding.pdfAccentColor)
    : null;

  const accent =
    (prefs.accentColor ? hexToRgb(prefs.accentColor) : null) ||
    clinicAccent ||
    clinicPrimary;
  const template: PdfTemplate = prefs.template || clinicTemplate || 'editorial';

  cachedBrand = {
    ...cachedBrand,
    hospitalName,
    tagline: branding.tagline,
    primary: accent,
    template,
    providerCredentials:
      prefs.credentialsLine?.trim() || getSignerIdentityLine(),
    signatureOverride: prefs.signatureImage || undefined,
  };

  if (branding.logo && branding.logo !== cachedLogoUrl) {
    cachedLogoUrl = branding.logo;
    const result = await fetchLogoToDataUrl(branding.logo);
    cachedBrand = {
      ...cachedBrand,
      logoDataUrl: result?.dataUrl ?? null,
      logoFormat: result?.format ?? 'PNG',
      logoAspect: result?.aspect ?? 1,
    };
  } else if (!branding.logo) {
    cachedLogoUrl = undefined;
    cachedBrand = { ...cachedBrand, logoDataUrl: null };
  }

  return cachedBrand;
};

/** Read the currently-cached brand without re-resolving. */
/**
 * The signer's printed identity (title · degrees · syndicate number) as the
 * clinic manager keeps it on their card. A doctor's own credentials line, when
 * they wrote one, wins; this fills in when they did not.
 */
let signerIdentity: { userId: string; tenantId: string; line: string } | null = null;

const sessionKey = () => {
  const st = useAuthStore.getState();
  return { userId: st.user?.id ?? '', tenantId: st.activeTenant?.id ?? '' };
};

/** The identity line, only while the session it was loaded for is the one signed in. */
export const getSignerIdentityLine = (): string | undefined => {
  if (!signerIdentity) return undefined;
  const k = sessionKey();
  return k.userId && signerIdentity.userId === k.userId && signerIdentity.tenantId === k.tenantId
    ? signerIdentity.line
    : undefined;
};

export const setSignerIdentityLine = (line: string | null | undefined): void => {
  const k = sessionKey();
  const t = line?.trim();
  signerIdentity = t && k.userId ? { ...k, line: t } : null;
};

export const getBrand = (): BrandAssets => ({
  ...cachedBrand,
  providerCredentials:
    usePdfPrefsStore.getState().credentialsLine?.trim() || getSignerIdentityLine(),
});

export interface EditorialHeaderOpts {
  /** Small uppercase kicker above the title (e.g. "PRESCRIPTION"). */
  eyebrow?: string;
  /** Large right-aligned document title. */
  title?: string;
  /** Back-compat alias for `title`. */
  docTitle?: string;
  /** Muted meta line under the title (e.g. patient · date). */
  subtitle?: string;
  /** Back-compat alias for `subtitle`. */
  meta?: string;
  /**
   * Per-document contact letterhead line under the clinic name (address ·
   * tel · email). Overrides the cached brand contactLine when provided.
   */
  contactLine?: string;
  /**
   * Accepted for back-compat with the previous header API. The confidential
   * treatment now lives in the footer (see drawEditorialFooter); this flag
   * is a no-op in the header and kept only so existing call sites compile.
   */
  showConfidential?: boolean;
}

const MARGIN = 14;

interface HeaderText {
  eyebrow?: string;
  title?: string;
  metaLine?: string;
  contactLine?: string;
}

const resolveHeaderText = (opts: EditorialHeaderOpts): HeaderText => ({
  eyebrow: opts.eyebrow,
  title: opts.title ?? opts.docTitle,
  metaLine: opts.subtitle ?? opts.meta,
  contactLine: opts.contactLine ?? cachedBrand.contactLine,
});

/** Place the clinic logo and return its rendered width (0 if none/failed). */
const placeLogo = (pdf: jsPDF, x: number, y: number, h: number): number => {
  const brand = cachedBrand;
  if (!brand.logoDataUrl) return 0;
  const w = Math.max(8, Math.min(h * 2.8, h * brand.logoAspect));
  try {
    pdf.addImage(brand.logoDataUrl, brand.logoFormat, x, y, w, h, undefined, 'FAST');
    return w;
  } catch {
    return 0;
  }
};

/**
 * EDITORIAL — logo + name top-left, eyebrow + large right-aligned title,
 * brand band, hairline with accent tick. The premium default.
 */
const headerEditorial = (pdf: jsPDF, t: HeaderText, accent: boolean): number => {
  const brand = cachedBrand;
  const pageW = pdf.internal.pageSize.getWidth();
  const [pr, pg, pb] = brand.primary;

  if (accent) {
    pdf.setFillColor(pr, pg, pb);
    pdf.rect(0, 0, pageW, 3.5, 'F');
  }

  const top = 13;
  const logoW = placeLogo(pdf, MARGIN, top, 12);
  const textX = logoW ? MARGIN + logoW + 5 : MARGIN;

  pdf.setTextColor(INK[0], INK[1], INK[2]);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text(brand.hospitalName, textX, top + 5.5);

  let leftY = top + 10;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  if (brand.tagline) {
    pdf.text(brand.tagline, textX, leftY);
    leftY += 4;
  }
  if (t.contactLine) {
    pdf.text(t.contactLine, textX, leftY);
    leftY += 4;
  }
  const leftBottom = Math.max(leftY, top + (logoW ? 12 : 6));

  const rightX = pageW - MARGIN;
  let rightBottom = top;
  if (t.eyebrow) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.setTextColor(pr, pg, pb);
    pdf.text(t.eyebrow.toUpperCase(), rightX, top + 3.5, { align: 'right' });
  }
  if (t.title) {
    // Title lives in the RIGHT column and is right-aligned. A long title
    // (e.g. a full research-report heading) must NOT run under the logo /
    // clinic name or off the left edge — so cap its width to the space
    // right of the clinic-name block, shrink the type to fit, and wrap to
    // as many lines as needed (growing downward).
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    const nameW = pdf.getTextWidth(brand.hospitalName);
    const titleMaxW = Math.max(60, rightX - (textX + nameW + 8));

    let titleSize = 19;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(titleSize);
    while (titleSize > 11 && pdf.getTextWidth(t.title) > titleMaxW) {
      titleSize -= 1;
      pdf.setFontSize(titleSize);
    }
    const titleLines: string[] = pdf.splitTextToSize(t.title, titleMaxW);
    const titleLineH = titleSize * 0.46;
    pdf.setTextColor(INK[0], INK[1], INK[2]);
    let ty = top + 12;
    for (const line of titleLines) {
      pdf.text(line, rightX, ty, { align: 'right' });
      ty += titleLineH;
    }
    rightBottom = ty - titleLineH;
  }
  if (t.metaLine) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    const metaY = rightBottom + 5;
    pdf.text(t.metaLine, rightX, metaY, { align: 'right' });
    rightBottom = metaY;
  }

  const ruleY = Math.max(leftBottom, rightBottom, top + 16) + 3;
  pdf.setDrawColor(HAIRLINE[0], HAIRLINE[1], HAIRLINE[2]);
  pdf.setLineWidth(0.4);
  pdf.line(MARGIN, ruleY, pageW - MARGIN, ruleY);
  pdf.setFillColor(pr, pg, pb);
  pdf.rect(MARGIN, ruleY - 0.5, 20, 1, 'F');
  pdf.setTextColor(0, 0, 0);
  return ruleY + 7;
};

/**
 * COMPACT — the editorial layout scaled down (thinner band, smaller logo +
 * type) so more content fits on a page. Same structure, less chrome.
 */
const headerCompact = (pdf: jsPDF, t: HeaderText): number => {
  const brand = cachedBrand;
  const pageW = pdf.internal.pageSize.getWidth();
  const [pr, pg, pb] = brand.primary;

  pdf.setFillColor(pr, pg, pb);
  pdf.rect(0, 0, pageW, 2.5, 'F');

  const top = 10;
  const logoW = placeLogo(pdf, MARGIN, top, 9);
  const textX = logoW ? MARGIN + logoW + 4 : MARGIN;

  pdf.setTextColor(INK[0], INK[1], INK[2]);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text(brand.hospitalName, textX, top + 4.5);
  let leftY = top + 9;
  if (t.contactLine) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    pdf.text(t.contactLine, textX, leftY);
    leftY += 3.5;
  }

  const rightX = pageW - MARGIN;
  if (t.eyebrow) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.setTextColor(pr, pg, pb);
    pdf.text(t.eyebrow.toUpperCase(), rightX, top + 3, { align: 'right' });
  }
  if (t.title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(INK[0], INK[1], INK[2]);
    pdf.text(t.title, rightX, top + 9, { align: 'right' });
  }
  if (t.metaLine) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    pdf.text(t.metaLine, rightX, top + 13, { align: 'right' });
  }

  const ruleY = Math.max(leftY, top + 14) + 2;
  pdf.setDrawColor(HAIRLINE[0], HAIRLINE[1], HAIRLINE[2]);
  pdf.setLineWidth(0.3);
  pdf.line(MARGIN, ruleY, pageW - MARGIN, ruleY);
  pdf.setFillColor(pr, pg, pb);
  pdf.rect(MARGIN, ruleY - 0.4, 16, 0.8, 'F');
  pdf.setTextColor(0, 0, 0);
  return ruleY + 5;
};

/**
 * LETTERHEAD — classic centred clinic identity (logo, name, contact) over a
 * centred document title. Conservative, what many doctors expect in print.
 */
const headerLetterhead = (pdf: jsPDF, t: HeaderText): number => {
  const brand = cachedBrand;
  const pageW = pdf.internal.pageSize.getWidth();
  const cx = pageW / 2;
  const [pr, pg, pb] = brand.primary;

  pdf.setFillColor(pr, pg, pb);
  pdf.rect(0, 0, pageW, 2.5, 'F');

  let y = 14;
  if (brand.logoDataUrl) {
    const h = 13;
    const w = Math.max(8, Math.min(h * 3, h * brand.logoAspect));
    try {
      pdf.addImage(brand.logoDataUrl, brand.logoFormat, cx - w / 2, y, w, h, undefined, 'FAST');
      y += h + 4;
    } catch {
      // text-only
    }
  }

  pdf.setTextColor(INK[0], INK[1], INK[2]);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text(brand.hospitalName, cx, y + 2, { align: 'center' });
  y += 6;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  if (brand.tagline) {
    pdf.text(brand.tagline, cx, y, { align: 'center' });
    y += 4;
  }
  if (t.contactLine) {
    pdf.text(t.contactLine, cx, y, { align: 'center' });
    y += 4;
  }

  y += 2;
  pdf.setDrawColor(HAIRLINE[0], HAIRLINE[1], HAIRLINE[2]);
  pdf.setLineWidth(0.4);
  pdf.line(MARGIN, y, pageW - MARGIN, y);
  pdf.setFillColor(pr, pg, pb);
  pdf.rect(cx - 10, y - 0.5, 20, 1, 'F');
  y += 8;

  if (t.eyebrow) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.setTextColor(pr, pg, pb);
    pdf.text(t.eyebrow.toUpperCase(), cx, y, { align: 'center' });
    y += 6;
  }
  if (t.title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(17);
    pdf.setTextColor(INK[0], INK[1], INK[2]);
    pdf.text(t.title, cx, y + 1, { align: 'center' });
    y += 7;
  }
  if (t.metaLine) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    pdf.text(t.metaLine, cx, y, { align: 'center' });
    y += 5;
  }
  pdf.setTextColor(0, 0, 0);
  return y + 4;
};

/**
 * PRE-PRINTED PAPER — the clinic's own letterhead is already on the sheet, so
 * nothing of the clinic's identity is drawn and the top 4 cm stays blank. The
 * document's own title block starts below that. Continuation pages leave the
 * same 4 cm: the paper is the same paper.
 */
export const PREPRINTED_TOP_MM = 40;
const headerPreprinted = (pdf: jsPDF, t: HeaderText): number => {
  const pageW = pdf.internal.pageSize.getWidth();
  let y = PREPRINTED_TOP_MM;
  if (t.title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(INK[0], INK[1], INK[2]);
    pdf.text(t.title, MARGIN, y + 5);
    y += 8;
  }
  if (t.metaLine) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    pdf.text(t.metaLine, pageW - MARGIN, PREPRINTED_TOP_MM + 5, { align: 'right' });
  }
  pdf.setDrawColor(HAIRLINE[0], HAIRLINE[1], HAIRLINE[2]);
  pdf.setLineWidth(0.3);
  pdf.line(MARGIN, y + 3, pageW - MARGIN, y + 3);
  pdf.setTextColor(0, 0, 0);
  return y + 9;
};

/**
 * Header dispatcher. Renders the resolved template (doctor → clinic →
 * default). Called with no opts on continuation pages — every variant then
 * draws only the clinic identity + rule (no title block), which is what an
 * overflow page wants. Returns the y to start body content at.
 */
export const drawEditorialHeader = (
  pdf: jsPDF,
  opts: EditorialHeaderOpts = {},
): number => {
  const t = resolveHeaderText(opts);
  switch (cachedBrand.template) {
    case 'letterhead':
      return headerLetterhead(pdf, t);
    case 'compact':
      return headerCompact(pdf, t);
    case 'preprinted':
      return headerPreprinted(pdf, t);
    case 'minimal':
      // Monochrome: editorial structure with the colour band suppressed;
      // the accent survives only as the title eyebrow + tick.
      return headerEditorial(pdf, t, false);
    default:
      return headerEditorial(pdf, t, true);
  }
};

export interface EditorialFooterOpts {
  confidential?: boolean;
}

/**
 * White-labelled footer applied to every page: hairline rule, clinic name
 * on the left, optional CONFIDENTIAL stamp centred, timestamp + pagination
 * on the right. No platform attribution — the document is the clinic's.
 */
export const drawEditorialFooter = (
  pdf: jsPDF,
  opts: EditorialFooterOpts = {},
): void => {
  const brand = cachedBrand;
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const total = pageCount(pdf);
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    pdf.setDrawColor(HAIRLINE[0], HAIRLINE[1], HAIRLINE[2]);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN, pageH - 12, pageW - MARGIN, pageH - 12);

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
    pdf.text(brand.hospitalName, MARGIN, pageH - 7);

    if (opts.confidential) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('CONFIDENTIAL', pageW / 2, pageH - 7, { align: 'center' });
    }

    pdf.setFont('helvetica', 'normal');
    pdf.text(
      `Generated ${fmtDateTz(new Date())}  ·  Page ${i} of ${total}`,
      pageW - MARGIN,
      pageH - 7,
      { align: 'right' },
    );
    pdf.setTextColor(0, 0, 0);
  }
};

/**
 * Stamp every page with a large diagonal watermark (e.g. "DRAFT —
 * UNSIGNED") so a printed/faxed copy of an unfinalized document can't be
 * mistaken for the real thing. Apply once at the end of the compose pass.
 */
export const addDraftWatermark = (
  pdf: jsPDF,
  label: string = 'DRAFT — UNSIGNED',
): void => {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const total = pageCount(pdf);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyPdf = pdf as any;
  const supportsGState =
    typeof anyPdf.GState === 'function' && typeof anyPdf.setGState === 'function';
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    if (supportsGState) {
      anyPdf.setGState(new anyPdf.GState({ opacity: 0.18 }));
    }
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(72);
    pdf.setTextColor(200, 30, 30);
    pdf.text(label, pageW / 2, pageH / 2, { align: 'center', angle: 30 });
    if (supportsGState) {
      anyPdf.setGState(new anyPdf.GState({ opacity: 1 }));
    }
    pdf.setTextColor(0, 0, 0);
  }
};
